import type { GroupStyleI, GroupStyleT } from "tweenly"
import { transforms as transformCatalogue } from "../data/styles/groups/transform.styles"

/**
 * Reading and writing an object's transform groups (rotation, flips). Pure: they return the
 * updated transforms for the caller to write.
 */

/** Degrees in any CSS angle unit the transform inputs offer. */
export const toDegrees = (value: number, unit?: string): number => {
    switch (unit) {
        case "rad":
            return (value * 180) / Math.PI
        case "grad":
            return value * 0.9
        case "turn":
            return value * 360
        default:
            return value
    }
}

/** An angle folded into (-180, 180], the way Figma shows rotation. */
export const normalizeDegrees = (degrees: number): number => {
    const folded = ((((degrees + 180) % 360) + 360) % 360) - 180
    return folded === -180 ? 180 : folded
}

export const roundTo2 = (value: number): number => Math.round(value * 100) / 100

export const findTransform = (
    transforms: GroupStyleI[],
    property: string
): GroupStyleI | undefined => transforms.find((transform) => transform.property === property)

/** The object's rotateZ in degrees, 0 when it has none. */
export const getRotation = (transforms: GroupStyleI[]): number => {
    const rotateZ = findTransform(transforms, "rotateZ")
    return rotateZ ? toDegrees(Number(rotateZ.value) || 0, rotateZ.unit) : 0
}

/**
 * Set a transform's value (and unit), adding the transform from the catalogue when the
 * object does not have it yet — it may have been removed in the Transforms section.
 */
export const setTransform = (
    transforms: GroupStyleI[],
    property: string,
    value: number,
    unit?: GroupStyleT["unit"]
): GroupStyleI[] => {
    const changes = unit === undefined ? { value } : { value, unit }
    if (findTransform(transforms, property)) {
        return transforms.map((transform) =>
            transform.property === property ? { ...transform, ...changes } : transform
        )
    }
    const template = transformCatalogue.find((transform) => transform.property === property)
    if (!template) return transforms
    const index =
        transforms.length === 0
            ? 0
            : Math.max(...transforms.map((transform) => transform.index)) + 1
    return [...transforms, { ...template, ...changes, index }]
}

/** Set rotateZ, in degrees folded into (-180, 180]. */
export const setRotation = (transforms: GroupStyleI[], degrees: number): GroupStyleI[] =>
    setTransform(transforms, "rotateZ", roundTo2(normalizeDegrees(degrees)), "deg")

/** Mirror the object along one axis by flipping the sign of its scale. */
export const flipScale = (transforms: GroupStyleI[], axis: "x" | "y"): GroupStyleI[] => {
    const property = axis === "x" ? "scaleX" : "scaleY"
    const current = Number(findTransform(transforms, property)?.value ?? 1)
    return setTransform(transforms, property, -(Number.isFinite(current) ? current : 1))
}
